import { signal } from '@preact/signals';

/**
 * The Android shell's JavaScript bridge, and nothing else.
 *
 * Two features ride on it — offline downloads and Chromecast — and both need the same thing
 * first: find `window.PodcastieNative`, which may not exist at all (every browser, and the iOS
 * PWA) and may arrive a beat late (a WebView without document-start scripts injects the shim at
 * page load instead). This module owns that detection so neither feature has to repeat it, and so
 * the single `onEvent` the bridge offers is registered exactly once and fanned out.
 *
 * Deliberately free of DOM types — see the note in `test/downloads-state.test.ts`.
 */

export type DownloadStatus = 'QUEUED' | 'RUNNING' | 'COMPLETE' | 'FAILED' | 'CANCELLED';

export interface DownloadRow {
  episodeId: string;
  status: DownloadStatus;
  bytesDone: number;
  bytesTotal: number;
  title?: string;
  error?: string;
}

/** A frame of the receiver's state, as `CastController.State.toJson` writes it. */
export interface CastStatus {
  /** A receiver is on the network, or one is already connected. */
  available: boolean;
  connected: boolean;
  connecting: boolean;
  deviceName: string | null;
  episodeId: string | null;
  playing: boolean;
  buffering: boolean;
  positionSec: number;
  durationSec: number;
  rate: number;
  /** The receiver reached the end of the episode; the phone advances its own queue. */
  finished: boolean;
}

/** What `castLoad` accepts. Mirrors `CastMedia.fromJson` in the Android app. */
export interface CastMediaSpec {
  id: string;
  title: string;
  feedTitle: string;
  imageUrl?: string | null;
  feedImageUrl?: string | null;
  enclosureUrl: string;
  durationSec: number;
  startAt: number;
  rate: number;
  autoplay: boolean;
}

/**
 * Absent on a shell older than bridge protocol 2, which is why every call site checks for the
 * namespace rather than reading the version number.
 */
export interface CastBridge {
  state(): Promise<CastStatus>;
  showPicker(): Promise<unknown>;
  load(episode: CastMediaSpec): Promise<unknown>;
  play(): Promise<unknown>;
  pause(): Promise<unknown>;
  seek(positionSec: number): Promise<unknown>;
  setRate(rate: number): Promise<unknown>;
  stop(): Promise<unknown>;
}

export interface NativeMediaMetadata {
  title: string;
  feedTitle?: string;
  imageUrl?: string | null;
  durationSec?: number;
  episodeId?: string | number;
}

export interface NativePlaybackState {
  playing: boolean;
  positionSec: number;
  durationSec: number;
  rate: number;
  buffering: boolean;
}

/** Mirrors `NativeBridge.SHIM_JS` in the Android app. Keep the two in step. */
export interface PodcastieNative {
  version: number;
  list(): Promise<DownloadRow[]>;
  download(episode: unknown): Promise<DownloadRow | null>;
  cancel(episodeId: string): Promise<unknown>;
  remove(episodeId: string): Promise<unknown>;
  removeAll(): Promise<unknown>;
  usage(): Promise<{ usedBytes: number; freeBytes: number }>;
  getWifiOnly(): Promise<boolean>;
  setWifiOnly(value: boolean): Promise<boolean>;
  onEvent(callback: (event: NativeEvent) => void): void;
  mediaMetadata?(meta: NativeMediaMetadata): Promise<unknown>;
  mediaPlaybackState?(state: NativePlaybackState): Promise<unknown>;
  mediaPlaybackStop?(): Promise<unknown>;
  cast?: CastBridge;
}

/** A push from the native side. `type` says which feature it belongs to. */
export interface NativeEvent {
  type?: string;
  row?: DownloadRow;
  state?: CastStatus;
  action?: 'play' | 'pause' | 'skipForward' | 'skipBack' | 'seekTo' | 'next' | 'stop';
  positionSec?: number;
}

/** True once the shell has been found. Stays false in every browser. */
export const hasNative = signal(false);

/**
 * The Android shell refuses cleartext network requests app-wide (no `usesCleartextTraffic`, no
 * network security config), while the web app's own CSP deliberately allows `http:` enclosures —
 * third-party feeds are not the app's to secure. A browser plays these fine; the shell's `<audio>`
 * element and its download bridge both fail on them, so both surfaces check for this up front
 * rather than surface whatever silent failure the underlying platform gives them (`row: null` from
 * the bridge, `net::ERR_CLEARTEXT_NOT_PERMITTED` with no listener from `<audio>`). Mirrors
 * `ERROR_INSECURE_ENCLOSURE` in Downloads.kt.
 */
export const INSECURE_ENCLOSURE_ERROR = 'insecure_enclosure';

/** Shared so every surface that reports [INSECURE_ENCLOSURE_ERROR] says the same thing. */
export const INSECURE_ENCLOSURE_MESSAGE =
  'This publisher serves audio over an insecure connection, which the app cannot play or download.';

/** True for a plain `http://` URL — the only enclosure scheme the shell blocks that the web app allows. */
export function isInsecureEnclosureUrl(url: string | null | undefined): boolean {
  return typeof url === 'string' && /^http:\/\//i.test(url);
}

/** The shim usually lands before any page script, but not on every WebView build. */
const READY_RETRY_MS = 250;
const READY_ATTEMPTS = 12;

let bridge: PodcastieNative | null = null;
let detect: ReturnType<typeof setTimeout> | null = null;
let bound = false;

const waiting: ((native: PodcastieNative) => void)[] = [];
const subscribers: ((event: NativeEvent) => void)[] = [];

function lookup(): PodcastieNative | null {
  const host = globalThis as { PodcastieNative?: PodcastieNative };
  return host.PodcastieNative ?? null;
}

/**
 * Runs `callback` once the bridge is available, and never at all if it is not.
 *
 * Synchronous when the shim is already installed, which is the normal case — a caller can rely on
 * having asked the bridge something by the time this returns.
 */
export function whenNative(callback: (native: PodcastieNative) => void): void {
  if (bridge) {
    callback(bridge);
    return;
  }
  waiting.push(callback);
  probe(0);
}

function probe(attempt: number): void {
  // Clear rather than orphan: a second caller arriving mid-chain would otherwise leave the old
  // chain ticking, and `resetNative` could only ever cancel the newest one.
  if (detect !== null) clearTimeout(detect);
  detect = null;

  const found = lookup();
  if (!found) {
    if (attempt < READY_ATTEMPTS) {
      detect = setTimeout(() => probe(attempt + 1), READY_RETRY_MS);
    }
    return;
  }

  bridge = found;
  hasNative.value = true;
  bind(found);
  // Splice first: a callback that calls back into `whenNative` must not re-enter this loop.
  for (const callback of waiting.splice(0)) callback(found);
}

function bind(native: PodcastieNative): void {
  if (bound) return;
  bound = true;
  native.onEvent((event) => {
    for (const subscriber of subscribers) {
      try {
        subscriber(event);
      } catch {
        // One feature's handler throwing must not cost the other its events.
      }
    }
  });
}

/** Subscribes to native pushes. Filter on `event.type` — every feature sees every frame. */
export function onNativeEvent(callback: (event: NativeEvent) => void): void {
  subscribers.push(callback);
}

/**
 * Test seam: drops all bridge state so each case starts from nothing.
 *
 * Only safe with a fresh bridge object — `onEvent` has no unsubscribe, so a listener registered
 * against a bridge that outlives the reset stays registered.
 */
export function resetNative(): void {
  bridge = null;
  bound = false;
  hasNative.value = false;
  waiting.length = 0;
  subscribers.length = 0;
  if (detect !== null) {
    clearTimeout(detect);
    detect = null;
  }
}
